import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { Icon, IconName } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { NakedButton, Text, styles, useTooltip } from '@frontend/design-system';
import { AutosaveStatus } from '../../../../draft/draft.types';

interface Props {
  status: AutosaveStatus;
}

type IconColor = 'subdued' | 'success' | 'warn' | 'error';

interface StatusPresentation {
  label: string;
  icon?: IconName;
  color: IconColor;
  /** Announced to assistive tech. Transient states (dirty/saving) are left out to avoid noise. */
  announce: boolean;
}

export const useAutosaveStatusPresentation = (status: AutosaveStatus): StatusPresentation => {
  const { t } = useTranslation('digital-forms-experience');

  switch (status) {
    case 'dirty':
      return { label: t('Changes not saved yet'), color: 'subdued', announce: false };
    case 'saving':
      return { label: t('Saving…'), icon: 'sync-small', color: 'subdued', announce: false };
    case 'saved':
      return { label: t('Draft saved'), icon: 'check-small', color: 'success', announce: true };
    case 'offline':
      return {
        label: t("You're offline. Recent changes aren't saved."),
        icon: 'wifi-small',
        color: 'warn',
        announce: true,
      };
    case 'retrying':
      return { label: t("Couldn't save. Retrying…"), icon: 'sync-small', color: 'warn', announce: true };
    case 'paused':
      return {
        label: t("Autosave paused. Recent changes aren't saved."),
        icon: 'pause-small',
        color: 'error',
        announce: true,
      };
    case 'unavailable':
      return {
        label: t('Autosave is unavailable. Keep this page open until you submit.'),
        icon: 'folder-error-small',
        color: 'warn',
        announce: true,
      };
    case 'submitting':
      return { label: t('Submitting…'), color: 'subdued', announce: false };
    case 'idle':
    default:
      return { label: t('Progress saves automatically'), color: 'subdued', announce: false };
  }
};

/** Compact, truthful autosave status shown below the form progress bar (spec §5.2). */
export const AutosaveStatusIndicator = ({ status }: Props) => {
  const { t } = useTranslation('digital-forms-experience');
  const { label, icon, color, announce } = useAutosaveStatusPresentation(status);
  const { Tooltip, triggerProps, tooltipProps } = useTooltip({ trigger: 'click', placement: 'bottom' });

  return (
    <div css={containerStyles} data-testid='autosave-status'>
      {icon && <Icon name={icon} size={16} color={color} css={iconStyles} />}
      {/* Single line keeps the header height fixed as the status changes; `title` exposes truncated text. */}
      <Text size='small' color={color === 'subdued' ? 'subdued' : 'default'} css={labelStyles} title={label}>
        {label}
      </Text>
      <NakedButton {...triggerProps} css={helpButtonStyles} aria-label={t('About autosave')}>
        <Icon name='info-small' size={16} color='subdued' />
      </NakedButton>
      <Tooltip {...tooltipProps}>
        {t(
          'Your progress is saved as a draft so you can return later. Select Submit when you’re finished so the office receives your information.'
        )}
      </Tooltip>
      <span css={styles.visuallyHidden} aria-live='polite' role='status'>
        {announce ? label : ''}
      </span>
    </div>
  );
};

const lineHeight = theme.spacing(2.5);

const containerStyles = css`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing(0.5)};
  height: ${lineHeight};
  min-width: 0;
  max-width: 100%;
`;

const iconStyles = css`
  flex-shrink: 0;
`;

const labelStyles = css`
  margin: 0;
  min-width: 0;
  line-height: ${lineHeight};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const helpButtonStyles = css`
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  height: ${lineHeight};
  padding: 0 ${theme.spacing(0.25)};
  border-radius: ${theme.borderRadius.small};

  &:focus-visible {
    outline: 2px solid ${theme.colors.primary50};
  }
`;
